// PaymentSandboxView — sandbox de testes do Mercado Pago.
// Admin gera Pix avulso e cobra cartão, sempre amarrado ao email
// configurado no backend (luis.moraesfh@gmail.com). Útil pra validar a
// integração end-to-end antes de soltar pra alunos.

import { useEffect, useState } from "preact/hooks";
import {
  AlertTriangle,
  Banknote,
  CheckCircle2,
  Copy,
  CreditCard,
  Loader2,
  QrCode,
  RefreshCw,
  Sparkles,
  Webhook
} from "lucide-react";
import { api } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { openAlert } from "./ConfirmPromptModal";

interface SandboxConfig {
  configured: boolean;
  message?: string;
  publicKey?: string | null;
  payerEmail?: string;
}

interface MpListItem {
  id: string;
  status: string;
  statusDetail: string;
  amountBrl: number;
  description: string;
  paymentMethod: string;
  externalReference: string;
  createdAt: string;
  approvedAt: string | null;
}

interface WebhookEvent {
  id: string;
  externalEventId: string;
  eventType: string;
  status: string;
  matchedChargeId: string | null;
  receivedAt: string;
  processedAt: string | null;
  processError: string | null;
  rawPayload: unknown;
}

interface PixResult {
  paymentId: string;
  brcode: string;
  qrCodeBase64?: string;
  ticketUrl?: string;
  expiresAt: string;
  externalReference: string;
}

let sdkLoadingPromise: Promise<void> | null = null;
function loadMpSdk(): Promise<void> {
  if (typeof window !== "undefined" && (window as any).MercadoPago) {
    return Promise.resolve();
  }
  if (sdkLoadingPromise) return sdkLoadingPromise;
  sdkLoadingPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://sdk.mercadopago.com/js/v2";
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Falha ao carregar SDK do Mercado Pago"));
    document.head.appendChild(s);
  });
  return sdkLoadingPromise;
}

export function PaymentSandboxView() {
  const [phase, setPhase] = useState<"loading" | "ready" | "error">("loading");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const [config, setConfig] = useState<SandboxConfig | null>(null);

  const [amountBrl, setAmountBrl] = useState("1.00");
  const [description, setDescription] = useState("Teste sandbox");

  // ── Pix
  const [pixLoading, setPixLoading] = useState(false);
  const [pixResult, setPixResult] = useState<PixResult | null>(null);

  // ── Cartão
  const [cardLoading, setCardLoading] = useState(false);
  const [cardResult, setCardResult] = useState<{
    paymentId: string;
    status: string;
    externalReference: string;
  } | null>(null);
  const [cardError, setCardError] = useState<string | null>(null);
  const [cardForm, setCardForm] = useState({
    number: "",
    holderName: "",
    holderDoc: "",
    expMonth: "",
    expYear: "",
    cvv: ""
  });

  // ── Lista MP + webhook events
  const [mpList, setMpList] = useState<MpListItem[]>([]);
  const [mpListLoading, setMpListLoading] = useState(false);
  const [events, setEvents] = useState<WebhookEvent[]>([]);

  async function loadConfig() {
    setPhase("loading");
    try {
      const r = await api.getPaymentSandboxConfig();
      setConfig(r);
      setPhase("ready");
      await loadList();
      await loadEvents();
    } catch (e) {
      setErrMsg(friendlyError(e));
      setPhase("error");
    }
  }

  async function loadList() {
    setMpListLoading(true);
    try {
      const r = await api.listPaymentSandbox();
      setMpList(r.results);
    } catch {
      // silent
    } finally {
      setMpListLoading(false);
    }
  }

  async function loadEvents() {
    try {
      const r = await api.listPaymentSandboxWebhookEvents();
      setEvents(r.events);
    } catch {
      // silent
    }
  }

  useEffect(() => {
    void loadConfig();
  }, []);

  function parseAmount(): number {
    const n = parseFloat(amountBrl.replace(",", "."));
    if (!isFinite(n) || n <= 0) return 0;
    return Math.round(n * 100);
  }

  async function generatePix() {
    setPixResult(null);
    const amountCents = parseAmount();
    if (amountCents <= 0) {
      await openAlert({ title: "Valor inválido", message: "Informe um valor > 0." });
      return;
    }
    setPixLoading(true);
    try {
      const r = await api.createPaymentSandboxPix({ amountCents, description });
      setPixResult(r.pix);
      void loadList();
    } catch (e) {
      await openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setPixLoading(false);
    }
  }

  async function chargeCard() {
    setCardError(null);
    setCardResult(null);
    const amountCents = parseAmount();
    if (amountCents <= 0) {
      setCardError("Valor inválido.");
      return;
    }
    if (!config?.publicKey) {
      setCardError("Public Key do MP não configurada.");
      return;
    }
    if (
      cardForm.number.length < 13 ||
      cardForm.cvv.length < 3 ||
      !cardForm.expMonth ||
      !cardForm.expYear ||
      cardForm.holderDoc.replace(/\D/g, "").length !== 11
    ) {
      setCardError("Preencha todos os campos do cartão (CPF 11 dígitos).");
      return;
    }
    setCardLoading(true);
    try {
      await loadMpSdk();
      const mp = new (window as any).MercadoPago(config.publicKey);
      const tokenResp = await mp.createCardToken({
        cardNumber: cardForm.number.replace(/\s/g, ""),
        cardholderName: cardForm.holderName,
        cardExpirationMonth: cardForm.expMonth.padStart(2, "0"),
        cardExpirationYear:
          cardForm.expYear.length === 2 ? `20${cardForm.expYear}` : cardForm.expYear,
        securityCode: cardForm.cvv,
        identificationType: "CPF",
        identificationNumber: cardForm.holderDoc.replace(/\D/g, "")
      });
      if (!tokenResp?.id) {
        setCardError(
          `Falha ao tokenizar: ${tokenResp?.message ?? "SDK não devolveu token"}`
        );
        return;
      }
      const r = await api.createPaymentSandboxCard({
        amountCents,
        description,
        mpCardToken: tokenResp.id
      });
      if (r.ok === false) {
        setCardError(r.reason ?? "Cobrança recusada.");
      } else {
        setCardResult({
          paymentId: r.paymentId,
          status: r.status,
          externalReference: r.externalReference
        });
      }
      void loadList();
    } catch (e: any) {
      const cause =
        e?.cause?.[0]?.description ?? e?.message ?? friendlyError(e);
      setCardError(cause);
    } finally {
      setCardLoading(false);
    }
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(
      () => void openAlert({ title: "Copiado!", message: "Brcode na área de transferência." }),
      () => void openAlert({ title: "Falha", message: "Seu navegador bloqueou a cópia." })
    );
  }

  if (phase === "loading") {
    return (
      <div class="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </div>
    );
  }
  if (phase === "error") {
    return (
      <div class="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
        {errMsg ?? "Falha ao carregar."}
      </div>
    );
  }

  if (!config?.configured) {
    return (
      <div class="space-y-4">
        <div class="rounded-md border border-amber-500/40 bg-amber-500/5 p-4">
          <div class="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 size-5 shrink-0 text-amber-600" />
            <div class="space-y-2 text-sm">
              <h3 class="font-bold">Mercado Pago não configurado</h3>
              <p class="text-muted-foreground">
                {config?.message ??
                  "Cole o Access Token (mpAccessToken) e a Public Key (mpPublicKey) em /manager → Métodos de pagamento (campo ccConfig). Depois ligue ccProvider=mercadopago e pixProvider=mercadopago. Sem isso, o sandbox não roda."}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div class="space-y-6">
      <div class="rounded-md border border-violet-500/30 bg-violet-500/5 p-4">
        <div class="flex items-start gap-3">
          <Sparkles className="mt-0.5 size-5 shrink-0 text-violet-600" />
          <div class="space-y-1 text-sm">
            <h3 class="font-bold">Sandbox Mercado Pago</h3>
            <p class="text-muted-foreground">
              Gera Pix e cobra cartão usando o gateway real do MP, sempre
              amarrado ao email <code class="font-mono">{config.payerEmail}</code>.
              Cobranças aparecem no seu painel MP e na lista abaixo. Use o app
              do banco pra pagar o Pix ou seus cartões de teste pra simular.
            </p>
            <p class="text-[11px] text-muted-foreground">
              ⚠️ Estamos em beta: <strong>nenhum aluno está sendo cobrado de
              verdade</strong>. Só esse painel admin gera cobranças reais — e
              elas vão pro seu email pra fechar o circuito.
            </p>
          </div>
        </div>
      </div>

      {/* ── Form com amount + description ─────────────────────── */}
      <section class="space-y-3 rounded-md border border-border bg-card p-5">
        <h2 class="text-sm font-bold">Dados do teste</h2>
        <div class="grid grid-cols-2 gap-3">
          <Field label="Valor (R$)">
            <input
              type="text"
              value={amountBrl}
              onInput={(e) => setAmountBrl((e.target as HTMLInputElement).value)}
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-violet-500"
              placeholder="1.00"
            />
          </Field>
          <Field label="Descrição">
            <input
              type="text"
              value={description}
              onInput={(e) => setDescription((e.target as HTMLInputElement).value)}
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-violet-500"
            />
          </Field>
        </div>
      </section>

      {/* ── Pix ───────────────────────────────────────────────── */}
      <section class="space-y-3 rounded-md border border-border bg-card p-5">
        <header class="flex items-center gap-2">
          <QrCode className="size-4 text-primary" />
          <h2 class="text-sm font-bold">Gerar Pix</h2>
        </header>
        <button
          type="button"
          onClick={generatePix}
          disabled={pixLoading}
          class="inline-flex items-center gap-2 rounded-md bg-violet-600 px-4 py-2 text-sm font-bold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {pixLoading ? <Loader2 className="size-4 animate-spin" /> : <QrCode className="size-4" />}
          Gerar Pix de teste
        </button>
        {pixResult && (
          <div class="space-y-3 rounded-md border border-emerald-500/40 bg-emerald-500/5 p-4">
            <div class="flex items-center gap-2 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
              <CheckCircle2 className="size-4" />
              Pix #{pixResult.paymentId} criado (ext_ref:{" "}
              <code class="font-mono text-[11px]">{pixResult.externalReference}</code>)
            </div>
            <div class="grid gap-4 md:grid-cols-[200px_1fr]">
              {pixResult.qrCodeBase64 && (
                <img
                  src={`data:image/png;base64,${pixResult.qrCodeBase64}`}
                  alt="QR Code"
                  class="h-48 w-48 rounded-md border border-border bg-white object-contain p-2"
                />
              )}
              <div class="space-y-2">
                <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  Brcode (copia-cola)
                </div>
                <textarea
                  readOnly
                  rows={5}
                  value={pixResult.brcode}
                  title="Pix brcode"
                  class="w-full rounded-md border border-border bg-background px-2 py-1.5 font-mono text-[10px]"
                />
                <div class="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => copyToClipboard(pixResult.brcode)}
                    class="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold hover:bg-muted"
                  >
                    <Copy className="size-3.5" />
                    Copiar brcode
                  </button>
                  {pixResult.ticketUrl && (
                    <a
                      href={pixResult.ticketUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      class="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-semibold hover:bg-muted"
                    >
                      Abrir no MP
                    </a>
                  )}
                </div>
                <div class="text-[11px] text-muted-foreground">
                  Expira em {new Date(pixResult.expiresAt).toLocaleString("pt-BR")}.
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* ── Cartão ────────────────────────────────────────────── */}
      <section class="space-y-3 rounded-md border border-border bg-card p-5">
        <header class="flex items-center gap-2">
          <CreditCard className="size-4 text-primary" />
          <h2 class="text-sm font-bold">Capturar cartão</h2>
        </header>
        <p class="text-[11px] text-muted-foreground">
          Cartão é tokenizado pelo SDK do MP no seu próprio browser — o PAN e o
          CVV nunca passam pelo nosso backend. Pra testar use os cartões de
          teste do MP:{" "}
          <code class="font-mono">5031 4332 1540 6351</code> (mastercard
          approved) ou{" "}
          <code class="font-mono">4509 9535 6623 3704</code> (visa). CVV
          qualquer 3 dígitos, validade futura, CPF teste{" "}
          <code class="font-mono">12345678909</code>.
        </p>
        <div class="grid grid-cols-2 gap-3 md:grid-cols-3">
          <Field label="Número">
            <input
              type="text"
              value={cardForm.number}
              onInput={(e) => setCardForm({ ...cardForm, number: (e.target as HTMLInputElement).value })}
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-violet-500"
              placeholder="5031 4332 1540 6351"
            />
          </Field>
          <Field label="Nome no cartão">
            <input
              type="text"
              value={cardForm.holderName}
              onInput={(e) => setCardForm({ ...cardForm, holderName: (e.target as HTMLInputElement).value })}
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-violet-500"
              placeholder="APRO TEST"
            />
          </Field>
          <Field label="CPF (somente números)">
            <input
              type="text"
              value={cardForm.holderDoc}
              onInput={(e) => setCardForm({ ...cardForm, holderDoc: (e.target as HTMLInputElement).value })}
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-violet-500"
              placeholder="12345678909"
            />
          </Field>
          <Field label="Mês (MM)">
            <input
              type="text"
              value={cardForm.expMonth}
              onInput={(e) => setCardForm({ ...cardForm, expMonth: (e.target as HTMLInputElement).value })}
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-violet-500"
              placeholder="12"
            />
          </Field>
          <Field label="Ano (AA ou AAAA)">
            <input
              type="text"
              value={cardForm.expYear}
              onInput={(e) => setCardForm({ ...cardForm, expYear: (e.target as HTMLInputElement).value })}
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-violet-500"
              placeholder="28"
            />
          </Field>
          <Field label="CVV">
            <input
              type="text"
              value={cardForm.cvv}
              onInput={(e) => setCardForm({ ...cardForm, cvv: (e.target as HTMLInputElement).value })}
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm font-mono outline-none focus:border-violet-500"
              placeholder="123"
            />
          </Field>
        </div>
        <button
          type="button"
          onClick={chargeCard}
          disabled={cardLoading}
          class="inline-flex items-center gap-2 rounded-md bg-violet-600 px-4 py-2 text-sm font-bold text-white hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {cardLoading ? <Loader2 className="size-4 animate-spin" /> : <CreditCard className="size-4" />}
          Cobrar cartão
        </button>
        {cardError && (
          <div class="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {cardError}
          </div>
        )}
        {cardResult && (
          <div class="rounded-md border border-emerald-500/40 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300">
            ✅ Cartão cobrado. Payment ID:{" "}
            <code class="font-mono text-[11px]">{cardResult.paymentId}</code>{" "}
            (ext_ref: {cardResult.externalReference})
          </div>
        )}
      </section>

      {/* ── Lista MP ──────────────────────────────────────────── */}
      <section class="space-y-3 rounded-md border border-border bg-card p-5">
        <header class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <Banknote className="size-4 text-primary" />
            <h2 class="text-sm font-bold">Pagamentos sandbox no MP</h2>
          </div>
          <button
            type="button"
            onClick={() => void loadList()}
            disabled={mpListLoading}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1 text-xs font-semibold hover:bg-muted"
          >
            <RefreshCw className={`size-3.5 ${mpListLoading ? "animate-spin" : ""}`} />
            Atualizar
          </button>
        </header>
        {mpList.length === 0 ? (
          <div class="rounded-md border border-dashed border-border bg-card/40 px-4 py-6 text-center text-[11px] text-muted-foreground">
            Nenhum pagamento sandbox ainda.
          </div>
        ) : (
          <div class="overflow-x-auto">
            <table class="w-full text-xs">
              <thead class="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th class="py-2">ID</th>
                  <th>Status</th>
                  <th>Método</th>
                  <th>Valor</th>
                  <th>Descrição</th>
                  <th>Criado</th>
                </tr>
              </thead>
              <tbody>
                {mpList.map((p) => (
                  <tr key={p.id} class="border-b border-border/50">
                    <td class="py-1.5 font-mono text-[10px]">{p.id}</td>
                    <td>
                      <StatusBadge status={p.status} />
                    </td>
                    <td class="font-mono text-[10px]">{p.paymentMethod}</td>
                    <td>R$ {p.amountBrl.toFixed(2)}</td>
                    <td class="max-w-[200px] truncate" title={p.description}>
                      {p.description}
                    </td>
                    <td class="text-[10px] text-muted-foreground">
                      {p.createdAt ? new Date(p.createdAt).toLocaleString("pt-BR") : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Webhook events ────────────────────────────────────── */}
      <section class="space-y-3 rounded-md border border-border bg-card p-5">
        <header class="flex items-center justify-between">
          <div class="flex items-center gap-2">
            <Webhook className="size-4 text-primary" />
            <h2 class="text-sm font-bold">Webhooks recebidos (últimos 20)</h2>
          </div>
          <button
            type="button"
            onClick={() => void loadEvents()}
            class="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1 text-xs font-semibold hover:bg-muted"
          >
            <RefreshCw className="size-3.5" />
            Atualizar
          </button>
        </header>
        {events.length === 0 ? (
          <div class="rounded-md border border-dashed border-border bg-card/40 px-4 py-6 text-center text-[11px] text-muted-foreground">
            Nenhum webhook recebido. Configure a URL no painel do MP:
            <br />
            <code class="font-mono text-[10px]">
              https://test-simulator-api.luismoraesfh.workers.dev/api/webhooks/mercadopago
            </code>
          </div>
        ) : (
          <ul class="space-y-2">
            {events.map((ev) => (
              <li
                key={ev.id}
                class="rounded-md border border-border bg-background/40 p-2 text-xs"
              >
                <div class="flex items-center justify-between">
                  <code class="font-mono text-[10px]">{ev.externalEventId}</code>
                  <StatusBadge status={ev.status} />
                </div>
                <div class="mt-1 text-[10px] text-muted-foreground">
                  {ev.eventType} ·{" "}
                  {new Date(ev.receivedAt).toLocaleString("pt-BR")}
                  {ev.processError && (
                    <span class="ml-2 text-destructive">• {ev.processError}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const lower = status.toLowerCase();
  const color =
    lower === "approved" || lower === "succeeded" || lower === "processed"
      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
      : lower === "pending" || lower === "in_process"
      ? "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-300"
      : lower === "rejected" || lower === "failed" || lower === "cancelled"
      ? "border-destructive/40 bg-destructive/10 text-destructive"
      : "border-border bg-muted text-muted-foreground";
  return (
    <span class={`inline-flex rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${color}`}>
      {status}
    </span>
  );
}

function Field({ label, children }: { label: string; children: any }) {
  return (
    <label class="block">
      <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}
